import _ from "lodash";

export function hasPricingContract(contract) {
	return Boolean(contract && Array.isArray(contract.documents) && contract.documents.length);
}

export function getSelectedContractDocument(contract) {
	if (!hasPricingContract(contract)) return null;
	const id = contract.activeDocumentId || contract.effectiveDocumentId;
	return id ? contract.documents.find(document => document.id === id) || null : contract.documents[0];
}

export function contractSaveConflict(message = "This home's agreement changed in another session. Your changes were not saved. Reopen the activity to review the latest agreement before applying them again.") {
	const error = new Error(message);
	error.code = "PRICING_CONTRACT_SAVE_CONFLICT";
	return error;
}

export function assertLockedContractDocumentsUnchanged(previous, next) {
	(previous && previous.documents || []).filter(document => document.status === "locked").forEach(document => {
		const candidate = next && next.documents && next.documents.find(item => item.id === document.id);
		if (!_.isEqual(document, candidate)) {
			throw contractSaveConflict("A finalized agreement cannot be changed or removed. Reopen the activity and select a draft addendum.");
		}
	});
}

export function assertContractBaseline(current, baseline, incoming) {
	// A retried exit with the identical result is harmless; an older snapshot is not.
	if (!_.isEqual(current || null, incoming || null) && !_.isEqual(current || null, baseline || null)) {
		throw contractSaveConflict();
	}
	assertLockedContractDocumentsUnchanged(current, incoming);
}

export function withSelectedContractInvoice(instance) {
	const document = getSelectedContractDocument(instance && instance.pricingContractProto);
	return document ? { ...instance, invoiceItems: _.cloneDeep(document.invoiceItems || []), jobDetails: _.cloneDeep(document.jobDetails || {}) } : instance;
}

// The installed v2 SDK predates PutObject.IfMatch. Add the header before signing,
// not as an unknown model parameter. Never retry a conflict without the condition.
export function guardContractPutRequest(request, etag) {
	if (!etag) throw contractSaveConflict("The saved agreement revision could not be verified. Reopen the activity before saving.");
	request.on("build", () => { request.httpRequest.headers["If-Match"] = etag; });
	return request;
}

export function mapContractWriteError(error) {
	return error && ([404, 409, 412].includes(error.statusCode) || error.code === "PreconditionFailed")
		? contractSaveConflict() : error;
}

export function createContractRevisionStore() {
	const revisions = new Map();
	return {
		remember(key, instance, etag) {
			const revision = { etag, contract: _.cloneDeep(instance && instance.pricingContractProto || null), projectId: instance && (instance.projectOps?.projectId || instance.projectOpsProjectId || instance.activityOps?.projectOps?.projectId) };
			revisions.set(key, revision);
			return revision;
		},
		get(key) { return revisions.get(key); },
		prepare(key, instance, expected = revisions.get(key)) {
			const projectId = instance && (instance.projectOps?.projectId || instance.projectOpsProjectId || instance.activityOps?.projectOps?.projectId) || expected?.projectId;
			if (!projectId) return null;
			if (!hasPricingContract(instance && instance.pricingContractProto) && !hasPricingContract(expected && expected.contract)) return null;
			if (!expected || !expected.etag) throw contractSaveConflict("The saved agreement revision could not be verified. Reopen the activity before saving.");
			if (hasPricingContract(instance.pricingContractProto) && !getSelectedContractDocument(instance.pricingContractProto)) {
				throw contractSaveConflict("The selected agreement no longer exists. Reopen the activity before saving.");
			}
			assertLockedContractDocumentsUnchanged(expected.contract, instance.pricingContractProto);
			return expected;
		}
	};
}
